import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, Filter, FileText, Eye, RefreshCw, Paperclip } from "lucide-react";
import { format } from "date-fns";
import PageHeader from '../components/PageHeader';
import PageLoader from '../components/PageLoader';
import EmptyState from '../components/EmptyState';
import StatusBadge from '../components/StatusBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRY_FLAGS = {
  "United Kingdom": "🇬🇧", "United States": "🇺🇸", "Canada": "🇨🇦",
  "United Arab Emirates": "🇦🇪", "Qatar": "🇶🇦", "Saudi Arabia": "🇸🇦",
  "Pakistan": "🇵🇰", "Malaysia": "🇲🇾", "Singapore": "🇸🇬",
  "Vietnam": "🇻🇳", "Azerbaijan": "🇦🇿", "Bahrain": "🇧🇭",
  "Oman": "🇴🇲", "Kuwait": "🇰🇼", "Turkey": "🇹🇷",
  "Thailand": "🇹🇭", "Indonesia": "🇮🇩", "China": "🇨🇳",
  "India": "🇮🇳", "Schengen (EU)": "🇪🇺",
};


const PAYMENT_STATUS_COLORS = {
  not_requested:   "bg-slate-100 text-slate-500",
  awaiting_payment:"bg-amber-100 text-amber-700",
  pay_later:       "bg-blue-100 text-blue-700",
  paid:            "bg-emerald-100 text-emerald-700",
};

const fmt = (n, currency = "EUR") =>
  `${currency === "EUR" ? "€" : currency} ${Number(n || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}`;

export default function VisaQuotesList() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/visa-quotes`, {
        headers: getAuthHeaders(),
      });
      setQuotes(response.data);
    } catch (error) {
      console.error("Failed to fetch visa quotes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  const filteredQuotes = quotes.filter((q) => {
    if (statusFilter !== "all" && (q.status || "draft") !== statusFilter) return false;
    const s = searchTerm.toLowerCase();
    return (
      !s ||
      q.customer_name?.toLowerCase().includes(s) ||
      q.customer_email?.toLowerCase().includes(s) ||
      q.quote_number?.toLowerCase().includes(s) ||
      q.destination_country?.toLowerCase().includes(s) ||
      q.visa_type?.toLowerCase().includes(s)
    );
  });

  const hasSupplierAttachments = (q) =>
    (q.accounting?.cost_items || []).some((item) => item.receipt_url);

  const openQuote = (quote) => {
    if (quote.visa_request_id) {
      navigate(`/visa-requests/${quote.visa_request_id}/quote`);
    } else {
      navigate(`/visa-requests`);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-8">
        {/* Header */}
        <PageHeader
          title="Visa Quotes"
          subtitle="View and manage all visa service quotations"
          actions={
            <Button onClick={fetchQuotes} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          }
        />

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name, email, quote #, country..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <PageLoader inline message="Loading visa quotes…" />
            ) : filteredQuotes.length === 0 ? (
              <EmptyState
                title="No visa quotes"
                description={
                  searchTerm || statusFilter !== "all"
                    ? "Try adjusting your filters"
                    : "Quotes are created from a visa request — open a request to generate one"
                }
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Quote #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Visa Type</TableHead>
                    <TableHead>Service Fee</TableHead>
                    <TableHead>Quote Status</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredQuotes.map((quote) => (
                    <TableRow
                      key={quote.id}
                      className="table-row-hover cursor-pointer"
                      onClick={() => openQuote(quote)}
                    >
                      <TableCell>
                        <span className="font-mono font-bold text-indigo-700">
                          {quote.quote_number || "—"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{quote.customer_name || "—"}</p>
                          <p className="text-sm text-slate-500">{quote.customer_email || ""}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                          <span>{COUNTRY_FLAGS[quote.destination_country] || "🌍"}</span>
                          {quote.destination_country || "—"}
                        </span>
                      </TableCell>
                      <TableCell>{quote.visa_type || "—"}</TableCell>
                      <TableCell>
                        <span className="font-mono text-sm font-semibold text-slate-700">
                          {fmt(quote.service_fee, quote.currency)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={quote.status || "draft"} />
                      </TableCell>
                      <TableCell>
                        <Badge className={PAYMENT_STATUS_COLORS[quote.payment_status] || "bg-slate-100 text-slate-500"}>
                          {(quote.payment_status || "not_requested").replace(/_/g, " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-500">
                          {quote.created_at
                            ? format(new Date(quote.created_at), "MMM d, yyyy")
                            : "—"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {hasSupplierAttachments(quote) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-blue-500 hover:text-blue-700"
                            title="Has supplier receipt attachments"
                            onClick={(e) => {
                              e.stopPropagation();
                              openQuote(quote);
                            }}
                          >
                            <Paperclip className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            openQuote(quote);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
